/**
 * Schema-based Handler Registry
 *
 * Registers dependency handlers for all fields based on schema,
 * not just for rendered components. This allows handlers to work
 * even for non-rendered array items (pagination).
 */

import { JSONSchema7 } from 'json-schema';
import { DEPENDENCY_MANAGER_ID_SEPARATOR } from '../utils';

/**
 * Finds all EntitySelectionPicker fields in the schema and returns
 * information about which fields they depend on
 */
export interface EntitySelectionPickerFieldInfo {
  fieldPath: string; // Full path like "dataAssets>[]>physicalAsset>developmentGroup"
  dependsOn: string; // Field it depends on, like "dataproducttest"
  schema: JSONSchema7; // The schema for this field
  pickerConfig: Record<string, any>;
}

const schemaBranchKeys = [
  'allOf',
  'anyOf',
  'oneOf',
  'if',
  'then',
  'else',
] as const;

function isEntitySelectionPicker(schema: Record<string, any>): boolean {
  return (
    schema['ui:widget'] === 'EntitySelectionPicker' ||
    schema['ui:field'] === 'EntitySelectionPicker'
  );
}

/**
 * Scans a schema recursively to find all EntitySelectionPicker fields
 */
export function findEntitySelectionPickerFields(
  schema: JSONSchema7,
  uiSchema: any = {},
  prefix: string = '',
  separator: string = DEPENDENCY_MANAGER_ID_SEPARATOR,
): EntitySelectionPickerFieldInfo[] {
  const fieldsByPath = new Map<string, EntitySelectionPickerFieldInfo>();

  const visit = (
    schemaNode: Record<string, any>,
    uiSchemaNode: Record<string, any>,
    currentPath: string,
  ) => {
    if (!schemaNode || typeof schemaNode !== 'object') return;

    const pickerConfig = { ...schemaNode, ...uiSchemaNode };
    if (currentPath && isEntitySelectionPicker(pickerConfig)) {
      const dependsOn = pickerConfig['ui:fieldName'];
      if (dependsOn && !fieldsByPath.has(currentPath)) {
        fieldsByPath.set(currentPath, {
          fieldPath: currentPath,
          dependsOn,
          schema: schemaNode as JSONSchema7,
          pickerConfig,
        });
      }
    }

    Object.entries(schemaNode.properties ?? {}).forEach(
      ([propertyName, propertySchema]) => {
        if (!propertySchema || typeof propertySchema !== 'object') return;
        const propertyPath = currentPath
          ? `${currentPath}${separator}${propertyName}`
          : propertyName;
        visit(
          propertySchema as Record<string, any>,
          uiSchemaNode?.[propertyName] ?? {},
          propertyPath,
        );
      },
    );

    const items = schemaNode.items;
    if (items && !Array.isArray(items) && typeof items === 'object') {
      const itemPath = currentPath ? `${currentPath}${separator}[]` : '[]';
      visit(items, uiSchemaNode?.items ?? {}, itemPath);
    }

    schemaBranchKeys.forEach(branchKey => {
      const branch = schemaNode[branchKey];
      const branches = Array.isArray(branch) ? branch : [branch];
      branches.forEach(branchSchema => {
        if (branchSchema && typeof branchSchema === 'object') {
          visit(branchSchema, uiSchemaNode, currentPath);
        }
      });
    });

    ['dependencies', 'dependentSchemas'].forEach(dependencyKey => {
      Object.values(schemaNode[dependencyKey] ?? {}).forEach(
        dependencySchema => {
          if (dependencySchema && typeof dependencySchema === 'object') {
            visit(
              dependencySchema as Record<string, any>,
              uiSchemaNode,
              currentPath,
            );
          }
        },
      );
    });
  };

  visit(schema as Record<string, any>, uiSchema, prefix);
  return Array.from(fieldsByPath.values());
}

/**
 * Expands a template field path to all actual array indices
 * e.g., "dataAssets>[]>physicalAsset>developmentGroup" becomes
 * ["dataAssets>0>physicalAsset>developmentGroup", "dataAssets>1>physicalAsset>developmentGroup", ...]
 */
export function expandTemplateFieldPath(
  templatePath: string,
  formData: any,
  separator: string = DEPENDENCY_MANAGER_ID_SEPARATOR,
): string[] {
  const results: string[] = [];
  const segments = templatePath.split(separator);

  const expand = (
    segmentIndex: number,
    currentValue: any,
    expandedSegments: string[],
  ) => {
    if (segmentIndex === segments.length) {
      results.push(expandedSegments.join(separator));
      return;
    }

    const segment = segments[segmentIndex];
    if (segment === '[]') {
      if (!Array.isArray(currentValue)) return;
      currentValue.forEach((item, itemIndex) => {
        expand(segmentIndex + 1, item, [
          ...expandedSegments,
          itemIndex.toString(),
        ]);
      });
      return;
    }

    const nextValue =
      currentValue && typeof currentValue === 'object'
        ? currentValue[segment]
        : undefined;
    expand(segmentIndex + 1, nextValue, [...expandedSegments, segment]);
  };

  expand(0, formData, []);

  return results;
}

/**
 * Creates a default handler for EntitySelectionPicker that resolves entities
 */
export function createDefaultEntitySelectionPickerHandler(
  dependsOn: string,
  getPropertyValues: (entity: any) => any,
  resolvePickerValue: (values: any[]) => any,
  schema: JSONSchema7,
): (
  fieldPath: string,
  triggerPath: string,
  triggerValue: any,
  currentFormData: any,
  config: any,
) => Promise<any> {
  return async (
    fieldPath: string,
    triggerPath: string,
    triggerValue: any,
    currentFormData: any,
    _config: any,
  ) => {
    // If the trigger is not our dependency, return current value
    if (triggerPath !== dependsOn && !triggerPath.includes(dependsOn)) {
      const currentValue = currentFormData[fieldPath];
      return currentValue !== undefined ? currentValue : schema.default;
    }

    // Use the trigger value as the referenced entity
    const referencedEntity = triggerValue;

    if (!referencedEntity) {
      return schema.default;
    }

    try {
      // Extract property values from the referenced entity
      const propertyValues = getPropertyValues(referencedEntity);

      // If we got an array of values, resolve them using the picker's resolver
      if (Array.isArray(propertyValues)) {
        const resolvedValue = resolvePickerValue(propertyValues);
        return resolvedValue !== undefined ? resolvedValue : schema.default;
      }

      // If we got a single value, return it directly
      if (propertyValues !== undefined && propertyValues !== null) {
        return propertyValues;
      }

      // If property extraction failed, return default instead of the full entity
      return schema.default;
    } catch (_error) {
      // On error, try to preserve current value or use default
      const currentValue = currentFormData[fieldPath];
      return currentValue !== undefined ? currentValue : schema.default;
    }
  };
}
